/**
 * Run-level undo, read and written through Wallet's OWN API.
 *
 * It lived in the extension's popup until 0.3.0 because the extension was the
 * only UI that knew runs existed. That is no longer true, and the move needs no
 * new verb on the capture port: `PATCH /api/points/capture/runs/{id}/review` is
 * this app's own authenticated route. `rejectRun` is deliberately NOT one of the
 * six verbs a web page may send to the extension -- a web page must not be able
 * to rewrite the ledger through the extension -- and that stays true, because
 * nothing here goes through the port at all.
 *
 * Narrower tools live elsewhere and are not this: the Points page has per-account
 * "Undo this reading" and per-credit Revert. This puts a whole RUN back.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";

/**
 * One run as Wallet records it: counts and dates, never a figure.
 *
 * The counts are what decide whether there is anything to undo, which is why the
 * page reads them from here rather than from the extension's snapshot -- the
 * extension reports what it SENT, and only Wallet knows what it kept.
 */
export interface CaptureRun {
  run_id: string;
  started_at: string | null;
  readings: number;
  created: number;
  rejected: number;
  held: number;
  instrument_changes: number;
  instruments_reverted: number;
  programs: string[];
}

/**
 * Whether this run still has anything an undo would put back.
 *
 * Both halves matter and both are net: a run whose new readings were already
 * rejected, or whose credit changes were already reverted, has nothing left --
 * and offering Undo there is a button that reports success having done nothing.
 * Same predicate the popup used, moved rather than rewritten.
 */
export function undoable(run: CaptureRun): boolean {
  return run.created - run.rejected + run.instrument_changes - run.instruments_reverted > 0;
}

/** The newest run, or null when Wallet has recorded none. */
export async function fetchNewestRun(): Promise<CaptureRun | null> {
  const res = await authedFetch("/api/points/capture/runs?limit=1");
  const body = (await orThrow(res, "Could not load the last run")) as { runs?: CaptureRun[] };
  return body.runs?.[0] ?? null;
}

/**
 * Undo a run: put its new readings back and revert the credit amounts it changed.
 *
 * The server decides what that means and refuses what it cannot do -- a credit
 * whose amount has moved again since is left alone, and an AwardWallet import
 * wrote no history so it cannot be reverted at all. This only asks.
 */
export async function rejectRun(runId: string): Promise<void> {
  const res = await authedFetch(
    `/api/points/capture/runs/${encodeURIComponent(runId)}/review`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: "rejected" }),
    },
  );
  await orThrow(res, "Could not undo the run");
}
